import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  ContributorMatch,
  ContributorMatchEditResource,
  ContributorMatchViewResource,
  LkMatchReviewId,
  Permission,
} from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { ContributorMatchPageBody } from "./ContributorMatchPageBody/ContributorMatchPageBody";
import { ContributorMatchPageHeader } from "./ContributorMatchPageHeader";

const routeName = "contributorMatch";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  contributorId?: number = 0;
  hideContributor?: boolean = false;
}

export const contributorMatchPageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Time & Effort",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const ContributorMatchPage = () => {
  const ROOT_PERMISSION: Permission = Permission.ContributorMatch;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [match, setMatch] = useState<ContributorMatch>({} as ContributorMatch);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [match] });
  const [viewResource, setViewResource] = useState<ContributorMatchViewResource>({});
  const [editResource, setEditResource] = useState<ContributorMatchEditResource>({} as ContributorMatchEditResource);
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [initialReviewId, setInitialReviewId] = useState<number>();
  const [willSendUnallowableEmail, setWillSendUnllowableEmail] = useState<boolean>(false);

  useEffect(() => {
    fetch(params.id);

    ApiService.contributorMatches.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit")
      ApiService.contributorMatches.getEditResource().then((response) => {
        setEditResource(response);
      });
  }, [params.crudMode]);

  // when review is changed to unallowable: set options to send email to created by user
  const canSendUnallowableEmail: boolean =
    match?.reviewId !== initialReviewId && match?.reviewId === Number(LkMatchReviewId.Unallowable);

  useEffect(() => {
    if (canSendUnallowableEmail) {
      setWillSendUnllowableEmail(true);
    } else {
      setWillSendUnllowableEmail(false);
    }
  }, [match?.reviewId]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${contributorMatchPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType })}`);

  const fetch = (id: number) => {
    ApiService.contributorMatches.get(id).then((response) => {
      if (!id && optionalParams.contributorId) response.contributorId = optionalParams.contributorId;

      setMatch(response);
      resetUnsavedChanges([response]);
      setInitialReviewId(response.reviewId);
    });
  };

  const pageHeader = (
    <ContributorMatchPageHeader
      rootPermission={ROOT_PERMISSION}
      handle={contributorMatchPageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      match={match}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
      canSendUnallowableEmail={canSendUnallowableEmail}
      willSendUnallowableEmail={willSendUnallowableEmail}
    />
  );

  const pageBody = (
    <ContributorMatchPageBody
      rootPermission={ROOT_PERMISSION}
      crudMode={params.crudMode}
      match={match}
      setMatch={setMatch}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
      onEdit={() => reroute(params.id, "edit")}
      hideContributor={optionalParams.hideContributor}
      canSendUnallowableEmail={canSendUnallowableEmail}
      willSendUnallowableEmail={willSendUnallowableEmail}
      setWillSendUnllowableEmail={setWillSendUnllowableEmail}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="lg"
      header={pageHeader}
      content={pageBody}
    />
  );
};
